import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { getLocations, createLocation, updateLocation, deleteLocation } from '../lib/api';
import { Plus, MapPin, MagnifyingGlass, SortAscending, SortDescending, ChartBar, X, PencilSimple, Trash, Broom, Palette, UploadSimple } from '@phosphor-icons/react';
import HelpAnchor from '../components/HelpAnchor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import LocationTransferDialog from '../components/LocationTransferDialog';
import { ArrowsLeftRight } from '@phosphor-icons/react';
import ZonePaletteDialog from '../components/ZonePaletteDialog';
import { useZonePalette } from '../lib/useZonePalette';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [zoneFilter, setZoneFilter] = useState('');
  const [fullnessSort, setFullnessSort] = useState('none'); // 'none' | 'full-first' | 'empty-first'
  const [showPalette, setShowPalette] = useState(false);
  const { style: zoneStyle, refresh: refreshPalette } = useZonePalette();
  const [newLoc, setNewLoc] = useState({
    zone: 'A', aisle: '01', bay: '01', level: '01', bin: '01',
    location_type: 'standard', capacity: 100, max_weight_kg: 500,
    length_cm: '', width_cm: '', height_cm: '',
  });
  // Controlled "Transfer from this bin" dialog state — one shared dialog
  // instance that every location card can open with its own preselected
  // source. Much cheaper than rendering 200 dialogs.
  const [xferFromLoc, setXferFromLoc] = useState(null);

  // iter160 — Edit + Delete dialog state
  const [editLoc, setEditLoc] = useState(null);   // location row being edited
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // iter161 — Bulk delete empty bins
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFilter, setBulkFilter] = useState({ warehouse_id: '', zone: '', aisle: '', location_type: '' });
  const [bulkPreview, setBulkPreview] = useState(null);   // {eligible_count, blocked_count, eligible, blocked, matched_total}
  const [bulkBusy, setBulkBusy] = useState(false);

  const runBulkPreview = async () => {
    setBulkBusy(true);
    try {
      const body = { ...Object.fromEntries(Object.entries(bulkFilter).filter(([_, v]) => v)), dry_run: true, limit: 500 };
      const r = await axios.post(`${API}/api/locations/bulk-delete-empty`, body, { withCredentials: true });
      setBulkPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setBulkBusy(false); }
  };
  const runBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const body = { ...Object.fromEntries(Object.entries(bulkFilter).filter(([_, v]) => v)), dry_run: false, limit: 500 };
      const r = await axios.post(`${API}/api/locations/bulk-delete-empty`, body, { withCredentials: true });
      toast.success(`${r.data.deleted_count} location(s) deleted${r.data.blocked_count ? ` · ${r.data.blocked_count} blocked` : ''}`);
      setBulkOpen(false);
      setBulkPreview(null);
      setBulkFilter({ warehouse_id: '', zone: '', aisle: '', location_type: '' });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkBusy(false); }
  };

  const openEdit = (loc) => {
    setEditLoc(loc);
    setEditForm({
      location_type: loc.location_type || 'standard',
      capacity: loc.capacity ?? 100,
      max_weight_kg: loc.max_weight_kg ?? 500,
      length_cm: loc.length_cm ?? '',
      width_cm: loc.width_cm ?? '',
      height_cm: loc.height_cm ?? '',
    });
  };
  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const body = {
        location_type: editForm.location_type,
        capacity: Number(editForm.capacity) || 0,
        max_weight_kg: Number(editForm.max_weight_kg) || 0,
        length_cm: editForm.length_cm === '' ? null : Number(editForm.length_cm),
        width_cm: editForm.width_cm === '' ? null : Number(editForm.width_cm),
        height_cm: editForm.height_cm === '' ? null : Number(editForm.height_cm),
      };
      await updateLocation(editLoc.id, body);
      toast.success(`${editLoc.location_code} updated`);
      setEditLoc(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setEditBusy(false); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    try {
      await deleteLocation(pendingDelete.id, forceDelete);
      toast.success(`${pendingDelete.location_code} deleted`);
      setPendingDelete(null);
      setForceDelete(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setDeleteBusy(false); }
  };

  // Volumetric utilization map: location_id -> {percent_full, has_capacity, used_m3, capacity_m3}
  // Loaded lazily via the bulk endpoint and refreshed alongside locations.
  const [utilByLoc, setUtilByLoc] = useState({});
  // Drilldown popup state — clicking a "% full" badge fires a single-bin
  // utilization fetch with a SKU breakdown and shows it inline.
  const [drilldownLoc, setDrilldownLoc] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const fetchData = async () => {
    try {
      const res = await getLocations({ zone: zoneFilter || undefined });
      setLocations(res.data);
      // Pull volumetric utilization in parallel — needs to be /api so we
      // hit the right router.
      try {
        const u = await axios.get(`${API}/api/locations/utilization-summary`, { withCredentials: true });
        const map = {};
        for (const r of (u.data?.rows || [])) map[r.location_id] = r;
        setUtilByLoc(map);
      } catch (_) { /* non-critical */ }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Load the SKU-level breakdown for one bin when its badge is clicked.
  const openDrilldown = async (loc) => {
    setDrilldownLoc(loc);
    setDrilldownData(null);
    setDrilldownLoading(true);
    try {
      const r = await axios.get(`${API}/api/locations/${loc.id}/utilization`, { withCredentials: true });
      setDrilldownData(r.data);
    } catch (e) {
      setDrilldownData({ error: e?.response?.data?.detail || 'Failed to load' });
    } finally {
      setDrilldownLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [zoneFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Coerce dim text inputs to number / null. Backend auto-derives
      // volume_m3 from L*W*H/1e6 when all three are set.
      const numOrNull = (v) => v === '' || v === null || v === undefined ? null : Number(v);
      await createLocation({
        ...newLoc,
        length_cm: numOrNull(newLoc.length_cm),
        width_cm: numOrNull(newLoc.width_cm),
        height_cm: numOrNull(newLoc.height_cm),
      });
      setShowCreate(false);
      setNewLoc({
        zone: 'A', aisle: '01', bay: '01', level: '01', bin: '01',
        location_type: 'standard', capacity: 100, max_weight_kg: 500,
        length_cm: '', width_cm: '', height_cm: '',
      });
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const toggleActive = async (loc) => {
    await updateLocation(loc.id, { is_active: !loc.is_active });
    fetchData();
  };

  // iter159 — load the effective location-code schema so the New Location
  // dialog only renders fields for enabled segments. Falls back to all-5
  // if the API isn't reachable.
  const [locSchema, setLocSchema] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/location-schemas/effective`, { withCredentials: true });
        setLocSchema(r.data);
      } catch (e) {
        setLocSchema({
          segments: [
            { key: 'zone', enabled: true, label: 'Zone' },
            { key: 'aisle', enabled: true, label: 'Aisle' },
            { key: 'bay', enabled: true, label: 'Bay' },
            { key: 'level', enabled: true, label: 'Level' },
            { key: 'bin', enabled: true, label: 'Bin' },
          ],
          separator: '-',
        });
      }
    })();
  }, []);

  const zones = [...new Set(locations.map(l => l.zone))];

  // Sort by fullness (client-side): occupancy pct = current_occupancy / capacity
  const sortedLocations = useMemo(() => {
    if (fullnessSort === 'none') return locations;
    const withPct = locations.map(l => ({
      loc: l,
      pct: l.capacity > 0 ? l.current_occupancy / l.capacity : 0,
    }));
    withPct.sort((a, b) =>
      fullnessSort === 'full-first' ? b.pct - a.pct : a.pct - b.pct
    );
    return withPct.map(x => x.loc);
  }, [locations, fullnessSort]);

  return (
    <Layout>
      <div data-testid="locations-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              Locations
              <HelpAnchor articleId="zone-palette" label="Zones, palette, and truck load planner" />
            </h1>
            <p className="text-muted-foreground">Manage warehouse bin locations</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <LocationTransferDialog onTransferred={fetchData} />
            <Button
              asChild
              variant="outline"
              data-testid="bulk-import-locations-btn"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Link to="/csv-import?entity=locations">
                <UploadSimple size={16} className="mr-1" /> Bulk import CSV
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => { setBulkOpen(true); setBulkPreview(null); }}
              data-testid="bulk-delete-empty-btn"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
            >
              <Broom size={16} className="mr-1" /> Bulk delete empty
            </Button>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-location-btn" className="bg-brand hover:bg-brand/90">
                <Plus size={18} className="mr-2" /> New Location
              </Button>
            </DialogTrigger>
            <DialogContent className="border border-border rounded-none">
              <DialogHeader>
                <DialogTitle>Create New Location</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                {/* iter159 — dynamic segment fields based on the effective schema */}
                <div
                  className={`grid gap-2`}
                  style={{ gridTemplateColumns: `repeat(${(locSchema?.segments || []).filter((s) => s.enabled).length || 1}, minmax(0, 1fr))` }}
                  data-testid="new-location-segments"
                >
                  {(locSchema?.segments || []).filter((s) => s.enabled).map((seg) => (
                    <div key={seg.key}>
                      <Label className="overline">{seg.label}</Label>
                      <Input
                        value={newLoc[seg.key] || ''}
                        onChange={(e) => setNewLoc({ ...newLoc, [seg.key]: e.target.value })}
                        required
                        className="mt-1 font-mono"
                        maxLength={seg.key === 'zone' ? 2 : 3}
                        data-testid={`new-location-${seg.key}`}
                      />
                    </div>
                  ))}
                </div>
                {locSchema && (
                  <div className="text-[11px] text-muted-foreground -mt-2 font-mono" data-testid="new-location-preview">
                    Preview: <span className="font-bold">
                      {locSchema.segments.filter((s) => s.enabled)
                        .map((s) => (newLoc[s.key] || '').trim() || `<${s.label}>`)
                        .join(locSchema.separator)}
                    </span>
                    {' '}<a href="/settings/location-schema" className="text-brand hover:underline ml-2">Edit schema →</a>
                  </div>
                )}
                <div>
                  <Label className="overline">Location Type</Label>
                  <Select value={newLoc.location_type} onValueChange={(v) => setNewLoc({...newLoc, location_type: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="bulk">Bulk</SelectItem>
                      <SelectItem value="picking">Picking</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="overline">Capacity (units)</Label>
                    <Input type="number" value={newLoc.capacity} onChange={(e) => setNewLoc({...newLoc, capacity: parseInt(e.target.value)})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="overline">Max Weight (kg)</Label>
                    <Input type="number" value={newLoc.max_weight_kg} onChange={(e) => setNewLoc({...newLoc, max_weight_kg: parseFloat(e.target.value)})} className="mt-1" />
                  </div>
                </div>
                {/* Physical dims — when all three are set the backend stores
                    volume_m3 (L×W×H/1e6) so the locations grid can show
                    "X% full" by SKU volume. Optional. */}
                <div>
                  <Label className="overline">Bin Dimensions (cm)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <Input type="number" min={0} step="0.1" placeholder="Length"
                      value={newLoc.length_cm}
                      onChange={(e) => setNewLoc({...newLoc, length_cm: e.target.value})}
                      className="font-mono" data-testid="new-location-length" />
                    <Input type="number" min={0} step="0.1" placeholder="Width"
                      value={newLoc.width_cm}
                      onChange={(e) => setNewLoc({...newLoc, width_cm: e.target.value})}
                      className="font-mono" data-testid="new-location-width" />
                    <Input type="number" min={0} step="0.1" placeholder="Height"
                      value={newLoc.height_cm}
                      onChange={(e) => setNewLoc({...newLoc, height_cm: e.target.value})}
                      className="font-mono" data-testid="new-location-height" />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {(() => {
                      const L = parseFloat(newLoc.length_cm) || 0;
                      const W = parseFloat(newLoc.width_cm) || 0;
                      const H = parseFloat(newLoc.height_cm) || 0;
                      if (L && W && H) return `Volume: ${((L * W * H) / 1_000_000).toFixed(3)} m³ — used to show % full per location`;
                      return 'Optional. Set all three to track volumetric capacity.';
                    })()}
                  </p>
                </div>
                <Button type="submit" className="w-full bg-brand hover:bg-brand/90">Create Location</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <Button variant={zoneFilter === '' ? 'default' : 'outline'} onClick={() => setZoneFilter('')} className={zoneFilter === '' ? 'bg-brand' : ''}>All</Button>
          {zones.map(z => {
            const s = zoneStyle(z);
            const active = zoneFilter === z;
            return (
              <Button
                key={z}
                variant={active ? 'default' : 'outline'}
                onClick={() => setZoneFilter(z)}
                style={active
                  ? { backgroundColor: s.bg, color: s.fg, borderColor: s.bg }
                  : { borderColor: s.bg, color: s.bg }}
                data-testid={`zone-filter-${z}`}
              >
                {s.icon ? `${s.icon} ` : ''}Zone {z}{s.configured && s.label !== s.code ? ` · ${s.label}` : ''}
              </Button>
            );
          })}
          <Button variant="outline" size="sm" onClick={() => setShowPalette(true)} data-testid="open-zone-palette" title="Edit zone palette (colours + labels)">
            <Palette size={14} className="mr-1" /> Palette
          </Button>
          <div className="ml-auto flex items-center gap-2" data-testid="locations-sort-wrapper">
            <Label className="overline text-xs text-muted-foreground">Sort by fullness</Label>
            <Select value={fullnessSort} onValueChange={setFullnessSort}>
              <SelectTrigger className="w-[180px] h-9" data-testid="locations-fullness-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="sort-option-none">Default order</SelectItem>
                <SelectItem value="full-first" data-testid="sort-option-full-first">
                  <span className="flex items-center gap-2"><SortDescending size={14} /> Full → Empty</span>
                </SelectItem>
                <SelectItem value="empty-first" data-testid="sort-option-empty-first">
                  <span className="flex items-center gap-2"><SortAscending size={14} /> Empty → Full</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            [1,2,3,4].map(i => <div key={i} className="h-40 bg-slate-200 animate-pulse"></div>)
          ) : sortedLocations.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              <MapPin size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No locations found</p>
            </div>
          ) : sortedLocations.map(loc => {
            const occupancy = loc.capacity > 0 ? (loc.current_occupancy / loc.capacity) * 100 : 0;
            const util = utilByLoc[loc.id];
            const zs = zoneStyle(loc.zone);
            return (
              <div key={loc.id} className={`bg-card border-l-4 p-4 ${loc.is_active ? 'border border-border' : 'border border-red-200 bg-red-50'}`} style={loc.is_active ? { borderLeftColor: zs.bg, borderLeftWidth: 4 } : undefined}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono font-bold text-lg">{loc.location_code}</p>
                    <p className="text-xs uppercase font-bold text-muted-foreground">{loc.location_type}</p>
                    <span
                      className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest"
                      style={{ backgroundColor: zs.bg, color: zs.fg }}
                      data-testid={`zone-chip-${loc.location_code}`}
                      title={zs.configured ? `${zs.label}` : `Zone ${zs.code} (unconfigured)`}
                    >
                      {zs.icon && <span>{zs.icon}</span>}{zs.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Volume utilization badge — clickable when capacity is set
                        so ops can drill down to "what's filling this bin?". */}
                    {util?.has_capacity && (
                      <button
                        type="button"
                        onClick={() => openDrilldown(loc)}
                        data-testid={`util-badge-${loc.location_code}`}
                        title={`${util.percent_full}% full · ${(util.used_m3 || 0).toFixed(3)} / ${(util.capacity_m3 || 0).toFixed(3)} m³ — click to see SKUs`}
                        className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border transition ${
                          util.percent_full >= 95 ? 'bg-rose-100 text-rose-800 border-rose-300 hover:bg-rose-200' :
                          util.percent_full >= 70 ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200' :
                          'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                        }`}
                      >
                        <ChartBar size={10} weight="bold" className="inline mr-1" />
                        {util.percent_full}% full
                      </button>
                    )}
                    {loc.current_occupancy > 0 && (
                      <button
                        type="button"
                        onClick={() => setXferFromLoc(loc.id)}
                        data-testid={`xfer-from-bin-${loc.location_code}`}
                        title={`Transfer stock from ${loc.location_code}`}
                        className="p-1.5 text-muted-foreground hover:text-brand hover:bg-muted rounded transition"
                        aria-label={`Transfer stock from ${loc.location_code}`}
                      >
                        <ArrowsLeftRight size={14} weight="bold" />
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(loc)}
                      data-testid={`edit-location-${loc.location_code}`}
                      title="Edit location"
                      className="p-1.5 text-muted-foreground hover:text-brand hover:bg-muted rounded transition"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => { setPendingDelete(loc); setForceDelete(false); }}
                      data-testid={`delete-location-${loc.location_code}`}
                      title="Delete location"
                      className="p-1.5 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 rounded transition"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => toggleActive(loc)}
                      className={`px-2 py-1 text-xs font-bold ${loc.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                    >
                      {loc.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </button>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Occupancy</span>
                    <span className="font-mono">{loc.current_occupancy}/{loc.capacity}</span>
                  </div>
                  <Progress value={occupancy} className="h-2" />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Max: {loc.max_weight_kg} kg</span>
                  <span className="font-mono bg-muted px-1">{loc.barcode?.slice(-8)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SKU breakdown popup — opens when a "% full" badge is clicked. */}
      <Dialog open={!!drilldownLoc} onOpenChange={(v) => { if (!v) { setDrilldownLoc(null); setDrilldownData(null); } }}>
        <DialogContent className="border border-border rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono">
              {drilldownLoc?.location_code} · capacity drill-down
            </DialogTitle>
          </DialogHeader>
          {drilldownLoading ? (
            <div className="text-sm text-muted-foreground py-6">Loading…</div>
          ) : drilldownData?.error ? (
            <div className="text-sm text-rose-700">{drilldownData.error}</div>
          ) : drilldownData ? (
            <div className="space-y-4" data-testid="util-drilldown-content">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="border border-border p-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Capacity</div>
                  <div className="font-mono font-bold">{(drilldownData.capacity_m3 || 0).toFixed(3)} m³</div>
                </div>
                <div className="border border-border p-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Used</div>
                  <div className="font-mono font-bold">{(drilldownData.used_m3 || 0).toFixed(3)} m³</div>
                </div>
                <div className="border border-border p-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">% Full</div>
                  <div className={`font-mono font-bold ${
                    (drilldownData.percent_full || 0) >= 95 ? 'text-rose-700' :
                    (drilldownData.percent_full || 0) >= 70 ? 'text-amber-700' : 'text-emerald-700'
                  }`}>
                    {drilldownData.percent_full ?? 0}%
                  </div>
                </div>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                Filled by ({drilldownData.by_sku?.length || 0} SKU{drilldownData.by_sku?.length === 1 ? '' : 's'} · {drilldownData.total_units} units)
              </div>
              {(drilldownData.by_sku || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Empty bin — no available stock.</p>
              ) : (
                <div className="border border-border max-h-[320px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-2">SKU</th>
                        <th className="text-left p-2">Name</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">Volume (m³)</th>
                        <th className="text-right p-2">% of bin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldownData.by_sku.map((row) => {
                        const pctOfBin = drilldownData.capacity_m3 > 0
                          ? (100 * row.volume_m3 / drilldownData.capacity_m3).toFixed(1)
                          : '0';
                        return (
                          <tr key={row.sku_id} className="border-t border-border" data-testid={`util-row-${row.sku_code}`}>
                            <td className="p-2 font-mono">{row.sku_code || row.sku_id?.slice(-8)}</td>
                            <td className="p-2 truncate max-w-[200px]">{row.sku_name || '—'}</td>
                            <td className="p-2 text-right tabular-nums">{row.qty}</td>
                            <td className="p-2 text-right tabular-nums">{(row.volume_m3 || 0).toFixed(3)}</td>
                            <td className="p-2 text-right tabular-nums">{pctOfBin}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Shared "Transfer from this bin" dialog — one instance, opened with
          a preselected From location from any card's icon button. */}
      <LocationTransferDialog
        open={!!xferFromLoc}
        onOpenChange={(v) => { if (!v) setXferFromLoc(null); }}
        preselectFromLocationId={xferFromLoc || undefined}
        onTransferred={() => { setXferFromLoc(null); fetchData(); }}
      />

      {/* iter160 — Edit location dialog (meta fields only — code is immutable) */}
      <Dialog open={!!editLoc} onOpenChange={(v) => !v && setEditLoc(null)}>
        <DialogContent className="border border-border rounded-none" data-testid="edit-location-dialog">
          <DialogHeader>
            <DialogTitle>Edit <code className="font-mono">{editLoc?.location_code}</code></DialogTitle>
            <DialogDescription>
              Location code is immutable (allocations and fixed-location rules reference it).
              Edit meta fields below; to change the code, delete and recreate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Location Type</Label>
              <Select value={editForm.location_type} onValueChange={(v) => setEditForm({ ...editForm, location_type: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-loc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="cold">Cold Storage</SelectItem>
                  <SelectItem value="hazardous">Hazardous</SelectItem>
                  <SelectItem value="quarantine">Quarantine</SelectItem>
                  <SelectItem value="receiving">Receiving</SelectItem>
                  <SelectItem value="shipping">Shipping</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="overline">Capacity (units)</Label>
                <Input type="number" min={1} value={editForm.capacity} onChange={(e) => setEditForm({ ...editForm, capacity: e.target.value })} data-testid="edit-loc-capacity" className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="overline">Max Weight (kg)</Label>
                <Input type="number" min={0} step={0.1} value={editForm.max_weight_kg} onChange={(e) => setEditForm({ ...editForm, max_weight_kg: e.target.value })} data-testid="edit-loc-max-weight" className="mt-1 font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="overline">Length (cm)</Label>
                <Input type="number" min={0} step={0.1} value={editForm.length_cm} onChange={(e) => setEditForm({ ...editForm, length_cm: e.target.value })} data-testid="edit-loc-length" className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="overline">Width (cm)</Label>
                <Input type="number" min={0} step={0.1} value={editForm.width_cm} onChange={(e) => setEditForm({ ...editForm, width_cm: e.target.value })} data-testid="edit-loc-width" className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="overline">Height (cm)</Label>
                <Input type="number" min={0} step={0.1} value={editForm.height_cm} onChange={(e) => setEditForm({ ...editForm, height_cm: e.target.value })} data-testid="edit-loc-height" className="mt-1 font-mono" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLoc(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editBusy} data-testid="edit-loc-save" className="bg-brand hover:bg-brand/90 text-white">
              {editBusy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter160 — Delete location alert dialog */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v) { setPendingDelete(null); setForceDelete(false); } }}>
        <AlertDialogContent data-testid="delete-location-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete <code className="font-mono">{pendingDelete?.location_code}</code>?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the bin. Inventory still on hand or any fixed-location
              rule will block the delete by default — clear them first.
              {pendingDelete?.current_occupancy > 0 && (
                <span className="block mt-2 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">
                  ⚠ This bin still shows <strong>{pendingDelete.current_occupancy}</strong> unit(s) on hand.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="delete-loc-force-wrap">
            <input type="checkbox" checked={forceDelete} onChange={(e) => setForceDelete(e.target.checked)} data-testid="delete-loc-force" className="mt-0.5" />
            <span>
              <span className="font-bold">Force delete</span> — override the on-hand check (useful for a stale empty bin whose counter is out of sync).
              Fixed-location rules will <em>still</em> block — repoint them first.
            </span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-loc-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleteBusy} data-testid="delete-loc-confirm" className="bg-rose-600 hover:bg-rose-700">
              {deleteBusy ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* iter161 — Bulk delete empty bins */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { if (!v) { setBulkOpen(false); setBulkPreview(null); } }}>
        <DialogContent className="border border-border rounded-none max-w-2xl" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Broom size={20} weight="duotone" className="text-rose-600" /> Bulk delete empty bins</DialogTitle>
            <DialogDescription>
              Filter by zone / aisle / type, preview which bins are eligible, then commit. Bins with on-hand stock, inventory batches, or fixed-location rules are automatically excluded.
            </DialogDescription>
          </DialogHeader>

          {!bulkPreview ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="overline">Zone (optional)</Label>
                  <Input value={bulkFilter.zone} onChange={(e) => setBulkFilter({ ...bulkFilter, zone: e.target.value })} placeholder="e.g. A" data-testid="bulk-zone" className="mt-1 font-mono" />
                </div>
                <div>
                  <Label className="overline">Aisle (optional)</Label>
                  <Input value={bulkFilter.aisle} onChange={(e) => setBulkFilter({ ...bulkFilter, aisle: e.target.value })} placeholder="e.g. 01" data-testid="bulk-aisle" className="mt-1 font-mono" />
                </div>
                <div className="col-span-2">
                  <Label className="overline">Location Type (optional)</Label>
                  <Select value={bulkFilter.location_type || '__any__'} onValueChange={(v) => setBulkFilter({ ...bulkFilter, location_type: v === '__any__' ? '' : v })}>
                    <SelectTrigger className="mt-1" data-testid="bulk-type"><SelectValue placeholder="Any type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any type</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="cold">Cold Storage</SelectItem>
                      <SelectItem value="hazardous">Hazardous</SelectItem>
                      <SelectItem value="quarantine">Quarantine</SelectItem>
                      <SelectItem value="receiving">Receiving</SelectItem>
                      <SelectItem value="shipping">Shipping</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">Leave all blank to match every empty bin (capped at 500 per pass).</div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button onClick={runBulkPreview} disabled={bulkBusy} data-testid="bulk-preview-btn">
                  {bulkBusy ? 'Searching…' : 'Preview'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="bulk-preview">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-border p-3"><div className="text-2xl font-bold tabular-nums">{bulkPreview.matched_total}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">empty matched</div></div>
                <div className="border border-emerald-200 bg-emerald-50 p-3"><div className="text-2xl font-bold tabular-nums text-emerald-700" data-testid="bulk-eligible-count">{bulkPreview.eligible_count}</div><div className="text-[10px] uppercase tracking-widest text-emerald-800">eligible</div></div>
                <div className="border border-amber-200 bg-amber-50 p-3"><div className="text-2xl font-bold tabular-nums text-amber-700" data-testid="bulk-blocked-count">{bulkPreview.blocked_count}</div><div className="text-[10px] uppercase tracking-widest text-amber-800">blocked</div></div>
              </div>

              {bulkPreview.eligible_count > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Will be deleted ({bulkPreview.eligible_count})</div>
                  <div className="max-h-[120px] overflow-y-auto border border-border bg-muted/20 p-2 font-mono text-[11px] flex flex-wrap gap-1" data-testid="bulk-eligible-list">
                    {bulkPreview.eligible.slice(0, 50).map((e) => <code key={e.id} className="bg-card border border-border px-1">{e.location_code}</code>)}
                    {bulkPreview.eligible.length > 50 && <span className="text-muted-foreground">…+{bulkPreview.eligible.length - 50} more</span>}
                  </div>
                </div>
              )}
              {bulkPreview.blocked_count > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-amber-800 mb-1">Blocked ({bulkPreview.blocked_count})</div>
                  <div className="max-h-[120px] overflow-y-auto border border-amber-200 bg-amber-50/50 p-2 text-[11px] space-y-0.5" data-testid="bulk-blocked-list">
                    {bulkPreview.blocked.slice(0, 30).map((b) => (
                      <div key={b.id}><code className="font-mono font-bold">{b.location_code}</code> — <span className="text-muted-foreground">{b.reason}</span></div>
                    ))}
                    {bulkPreview.blocked.length > 30 && <div className="text-muted-foreground">…+{bulkPreview.blocked.length - 30} more</div>}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkPreview(null)} data-testid="bulk-back-btn">← Back to filters</Button>
                <Button
                  onClick={runBulkDelete}
                  disabled={bulkBusy || bulkPreview.eligible_count === 0}
                  data-testid="bulk-confirm-btn"
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  {bulkBusy ? 'Deleting…' : `Delete ${bulkPreview.eligible_count} bin(s)`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ZonePaletteDialog
        open={showPalette}
        onClose={() => setShowPalette(false)}
        onSaved={refreshPalette}
      />
    </Layout>
  );
}
